import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Modal from "../../../components/Modal/Modal";
import SheetActivityItem from "../SheetActivityItem/SheetActivityItem";
import { FONT_GLOBO } from "../../../styles/fonts";
import {
  ACTIVITY_TYPE_OPTIONS,
  filterActivities,
} from "./activitiesFilter.utils";

const filterBarSx = {
  flexShrink: 0,
  padding: "24px 24px 12px 24px",
  borderBottom: "1px solid #e0e0e0",
};

const chipsRowSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "8px",
  marginTop: "16px",
};

const listSx = {
  flex: "1 1 auto",
  overflowY: "auto",
};

const emptyStateSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "48px 24px",
  color: "#7c7c8a",
};

const ActivitiesCompletedModal = ({
  open,
  onClose,
  activities,
  contentType,
  format,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);

  const filteredList = useMemo(
    () => filterActivities(activities, { searchTerm, selectedTypes }),
    [activities, searchTerm, selectedTypes]
  );

  // Keep every item mounted (never remove from the DOM) so filtering can't unmount a
  // SheetActivityItem, which would otherwise trigger its close-on-unmount side effect.
  // Track visibility by object reference (not item.id) since ids aren't guaranteed unique.
  const visibleItems = useMemo(() => new Set(filteredList), [filteredList]);

  const toggleType = (type) =>
    setSelectedTypes((current) =>
      current.includes(type)
        ? current.filter((selected) => selected !== type)
        : [...current, type]
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Atividades Concluídas"
      maxWidth="640px"
      maxHeight="90vh"
      bodySx={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={filterBarSx}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar por nome do analista, capítulo, bloco ou data"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontFamily: `${FONT_GLOBO} !important` },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: !!searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Limpar busca"
                  onClick={() => setSearchTerm("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontFamily: "inherit",
            display: "block",
            marginTop: "8px",
            fontSize: "12px",
          }}
        >
          Use o símbolo + para combinar termos - ex. João + bloco 11
        </Typography>

        <Box sx={chipsRowSx}>
          {ACTIVITY_TYPE_OPTIONS.map((type) => {
            const selected = selectedTypes.includes(type);

            return (
              <Chip
                key={type}
                label={type}
                clickable
                variant="outlined"
                onClick={() => toggleType(type)}
                sx={{
                  fontFamily: "inherit",
                  borderRadius: "8px",
                  borderColor: "#007EB5",
                  color: selected ? "#fff" : "#007EB5",
                  backgroundColor: selected ? "#007EB5" : "transparent",
                  // MUI's own ".MuiChip-clickable.MuiChip-outlined:hover" rule outranks a plain sx hover class, so force it.
                  "&:hover": {
                    backgroundColor: selected
                      ? "#006199 !important"
                      : "rgba(0, 126, 181, 0.08) !important",
                    borderColor: "#007EB5 !important",
                    color: selected ? "#fff !important" : "#007EB5 !important",
                  },
                }}
              />
            );
          })}

          {!!selectedTypes.length && (
            <Chip
              label="Limpar"
              clickable
              variant="outlined"
              onClick={() => setSelectedTypes([])}
              sx={{
                fontFamily: "inherit",
                borderRadius: "8px",
                marginLeft: "auto",
                borderColor: "#9e9e9e",
                color: "#757575",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                  borderColor: "#9e9e9e !important",
                  color: "#757575 !important",
                },
              }}
            />
          )}
        </Box>

        {!!filteredList.length && (
          <Typography
            variant="body2"
            sx={{ fontFamily: "inherit", marginTop: "24px", color: "#5c5c66" }}
          >
            {filteredList.length}{" "}
            {filteredList.length === 1
              ? "atividade encontrada"
              : "atividades encontradas"}
          </Typography>
        )}
      </Box>

      <Box sx={listSx}>
        {activities.map((item, index) => (
          <Box
            key={item.id ?? index}
            sx={{ display: visibleItems.has(item) ? "block" : "none" }}
          >
            <SheetActivityItem
              item={item}
              contentType={contentType}
              format={format}
              openLinksInNewTab
            />
          </Box>
        ))}

        {!filteredList.length && (
          <Box sx={emptyStateSx}>
            <SearchIcon fontSize="large" />
            <Typography variant="body2" sx={{ fontFamily: "inherit" }}>
              Nenhuma atividade encontrada
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ActivitiesCompletedModal;
